/**
 * Timezone utilities for the Cricket Academy frontend.
 * Handles date/time formatting based on the configured business timezone.
 */

// Cache for timezone settings
let timezoneCache = null;
let lastFetched = null;
const CACHE_TTL = 300000; // 5 minutes in milliseconds

/**
 * Get the configured timezone from the API
 */
export const getBusinessTimezone = async (api) => {
  const now = Date.now();
  
  // Check cache
  if (timezoneCache && lastFetched && (now - lastFetched) < CACHE_TTL) {
    return timezoneCache;
  }
  
  try {
    const response = await api.get('/admin/timezone-settings');
    timezoneCache = response.data.timezone || 'America/New_York';
    lastFetched = now;
    return timezoneCache;
  } catch (error) {
    // Return default timezone if API fails
    return 'America/New_York';
  }
};

/**
 * Get the current date in the business timezone as YYYY-MM-DD string
 */
export const getCurrentDateString = (timezone = 'America/New_York') => {
  try {
    const now = new Date();
    // Format in the specified timezone
    const options = { 
      timeZone: timezone, 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    };
    const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(now);
    
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    // Fallback to local date
    const now = new Date();
    return now.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format
  }
};

/**
 * Get the current time in the business timezone as HH:MM string
 */
export const getCurrentTimeString = (timezone = 'America/New_York') => {
  try {
    const now = new Date();
    const options = { 
      timeZone: timezone, 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    };
    return new Intl.DateTimeFormat('en-US', options).format(now);
  } catch (error) {
    // Fallback
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  }
};

/**
 * Format a date string or Date object in the business timezone
 */
export const formatDateInTimezone = (date, timezone = 'America/New_York', options = {}) => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    
    const defaultOptions = { 
      timeZone: timezone,
      ...options
    };
    
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  } catch (error) {
    return date?.toString() || '';
  }
};

/**
 * Format datetime for display (e.g., "Mar 8, 2025 3:30 PM")
 */
export const formatDateTimeDisplay = (date, timezone = 'America/New_York') => {
  return formatDateInTimezone(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date for display (e.g., "Mar 8, 2025")
 */
export const formatDateDisplay = (date, timezone = 'America/New_York') => {
  return formatDateInTimezone(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format time for display (e.g., "3:30 PM")
 */
export const formatTimeDisplay = (date, timezone = 'America/New_York') => {
  return formatDateInTimezone(date, timezone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Convert a local date string to start of day in the business timezone
 */
export const toTimezoneStartOfDay = (dateString, timezone = 'America/New_York') => {
  try {
    // Create date at midnight in the specified timezone
    const date = new Date(dateString + 'T00:00:00');
    return date;
  } catch (error) {
    return new Date(dateString);
  }
};

/**
 * Check if a date is today in the business timezone
 */
export const isToday = (date, timezone = 'America/New_York') => {
  const today = getCurrentDateString(timezone);
  const checkDate = typeof date === 'string' ? date.split('T')[0] : 
    formatDateInTimezone(date, timezone, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return today === checkDate;
};

/**
 * Clear the timezone cache (call when timezone settings are updated)
 */
export const clearTimezoneCache = () => {
  timezoneCache = null;
  lastFetched = null;
};

/**
 * Convert a UTC ISO datetime string to a datetime-local input value in the business timezone
 * Input: "2026-04-05T01:00:00+00:00" (UTC)
 * Output: "2026-04-04T21:00" (EST, format for datetime-local input)
 */
export const utcToLocalInputValue = (utcIsoString, timezone = 'America/New_York') => {
  if (!utcIsoString) return '';
  try {
    const date = new Date(utcIsoString);
    if (isNaN(date.getTime())) return '';
    
    // Get the date/time components in the target timezone
    const options = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    const hour = parts.find(p => p.type === 'hour')?.value;
    const minute = parts.find(p => p.type === 'minute')?.value;
    
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch (error) {
    console.error('Error converting UTC to local:', error);
    return utcIsoString.slice(0, 16);
  }
};

/**
 * Convert a datetime-local input value (in business timezone) to UTC ISO string
 * Input: "2026-04-04T21:00" (entered as EST)
 * Output: "2026-04-05T02:00:00.000Z" (UTC, during EST, +5 hours; during EDT +4 hours)
 */
export const localInputValueToUtc = (localDatetimeValue, timezone = 'America/New_York') => {
  if (!localDatetimeValue) return null;
  try {
    // Parse the datetime-local value
    const [datePart, timePart] = localDatetimeValue.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    
    // Create a date formatter to get the timezone offset for this specific date
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset'
    });
    
    // Create a date in UTC first, then we'll adjust
    // We need to find the offset at this particular date/time
    const tempDate = new Date(Date.UTC(year, month - 1, day, hour, minute));
    
    // Get the offset for this timezone at this date
    // This accounts for DST changes
    const tzParts = formatter.formatToParts(tempDate);
    const offsetPart = tzParts.find(p => p.type === 'timeZoneName')?.value || '';
    
    // Parse offset like "GMT-4" or "GMT-5" or "GMT+5:30"
    const offsetMatch = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    let offsetMinutes = 0;
    if (offsetMatch) {
      const sign = offsetMatch[1] === '+' ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const mins = parseInt(offsetMatch[3] || '0', 10);
      offsetMinutes = sign * (hours * 60 + mins);
    }
    
    // Create the UTC date by subtracting the offset
    // If EST is UTC-5, and user enters 21:00 EST, that's 21:00 + 5 hours = 02:00 UTC next day
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute) - (offsetMinutes * 60 * 1000));
    
    return utcDate.toISOString();
  } catch (error) {
    console.error('Error converting local to UTC:', error);
    // Fallback to browser's interpretation
    return new Date(localDatetimeValue).toISOString();
  }
};

/**
 * Format a date-only string (YYYY-MM-DD) for display without timezone shift
 * This handles the case where we just have a date without time
 */
export const formatDateOnly = (dateString, options = {}) => {
  if (!dateString) return '';
  
  // If it's a simple YYYY-MM-DD string, parse it without timezone conversion
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date at noon to avoid any DST issues
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    const defaultOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    return date.toLocaleDateString('en-US', defaultOptions);
  }
  
  // For ISO strings with time, use the regular formatter
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', options);
};

export default {
  getBusinessTimezone,
  getCurrentDateString,
  getCurrentTimeString,
  formatDateInTimezone,
  formatDateTimeDisplay,
  formatDateDisplay,
  formatTimeDisplay,
  toTimezoneStartOfDay,
  isToday,
  clearTimezoneCache,
  utcToLocalInputValue,
  localInputValueToUtc,
  formatDateOnly
};
